export interface FileTypeInfo {
    mime: string;
    extension: string;
}

const mimeMap: Record<string, string> = {
    "application/pdf": "pdf",
    "application/msword": "doc",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
    "application/vnd.ms-excel": "xls",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
    "text/csv": "csv",
    "image/jpeg": "jpg",
    "image/png": "png"
};

/**
 * Gets the content type from URL parameters
 */
function getContentTypeFromUrl(docUrl: string): string | null {
    try {
        const parsedUrl = new URL(docUrl);
        const type = parsedUrl.searchParams.get('response-content-type');
        return type ? decodeURIComponent(type) : null;
    } catch (err) {
        console.error('Invalid URL:', err);
        return null;
    }
}

/**
 * Determines file type information from URL and document name
 */
export function getFileTypeInfo(docUrl: string, documentName?: string): FileTypeInfo {
    if (!docUrl) {
        return { mime: '', extension: '' };
    }

    // Get MIME type from URL parameters
    const mime = getContentTypeFromUrl(docUrl);
    
    // If MIME type is found in our map, return it
    if (mime && mime in mimeMap) {
        return { mime, extension: mimeMap[mime] };
    }

    // If MIME type not found in most popular content types,
    // get extension from filename
    const path = docUrl.split('?')[0];
    const filenameWithExt = path.split('/').pop();
    const finalDocumentName = (!documentName || documentName.trim() === '') ? filenameWithExt : documentName;

    if (!finalDocumentName) {
        return { mime: mime || '', extension: '' };
    }

    const parts = finalDocumentName.split('.');
    const extension = parts.pop() || '';

    return { mime: mime || '', extension };
}

/**
 * Checks if the file is a PDF
 */
export function isPdfFile(fileInfo: FileTypeInfo): boolean {
    return fileInfo.extension.toLowerCase() === 'pdf' || fileInfo.mime === 'application/pdf';
}

/**
 * Checks if the file is an image
 */
export function isImageFile(fileInfo: FileTypeInfo): boolean {
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
    const imageMimes = ['image/jpeg', 'image/png', 'image/gif', 'image/bmp', 'image/webp', 'image/svg+xml'];
    
    return imageExtensions.includes(fileInfo.extension.toLowerCase()) || 
           imageMimes.includes(fileInfo.mime.toLowerCase());
}

/**
 * Checks if the file can be viewed inline (PDF or images)
 */
export function canViewInline(fileInfo: FileTypeInfo): boolean {
    return isPdfFile(fileInfo) || isImageFile(fileInfo);
}
